import { Select, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  createStudentAwards,
  getAwards,
  getStudentAwards,
} from '../../../api/award.api';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import {
  IAwardItem,
  IGetAwards,
  IGetStudentAwards,
  IStudentAward,
} from '../../../models/award.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import AwardHistoryModal from './modals/AwardHistoryModal';
import DownloadPDFButton from "../../DownloadPDFButton";
import MakeAwardPDFModal from './modals/MakeAwardPDFModal';

const ClassAward = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetAwards>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetStudentAwards>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [data, setData] = useState<IStudentAward[]>([]);
  const [awardHistoryVisible, setAwardHistoryVisible] = useState(false);
  const [memberCode, setMemberCode] = useState('');
  const [selectsKeys, setSelectsKeys] = useState<React.Key[]>([]);
  const [visiblePdfDownload, setVisiblePdfDownload] = useState(false);
  const [selAwardList, setSelAwardList] = useState<Map<string, IAwardItem>>(new Map<string, IAwardItem>());

  useEffect(() => {
    execute(getAwards());
  }, []);

  useEffect(() => {
    if (chosenClass) {
      execute2(getStudentAwards(chosenClass.classCode));
    }
  }, [chosenClass]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      setData(_.cloneDeep(res2.data));
      initSelects();
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  const onSetAwardCode = (index: number, value: string, rowData: any) => {
    const newData = [...data];
    newData[index].awardCode = value;
    setData(newData);

    if(selAwardList.get(rowData.memberCode)) {
      const tempAwardList : Map<string, IAwardItem> = new Map(selAwardList.entries());
      tempAwardList.set(rowData.memberCode, {awardCode:value, name:rowData.memberEngName, memo:''}) 
      setSelAwardList(tempAwardList);
    }
  };

  const onSubmit = () => {
    if (!chosenClass || !res2?.data) return;
    const req: any = [];

    data.forEach((item) => {
      if (item.awardCode) {
        req.push({
          memberCode: item.memberCode,
          awardCode: item.awardCode,
        });
      }
    });

    if (req.length > 0) {
      submitExecute(createStudentAwards(chosenClass.classCode, req));
    }
  };

  const initSelects = () => {
    setSelectsKeys([]);
  }

  const rowSelection = {
      selectedRowKeys: selectsKeys,
      onChange: (selectedRowKeys: React.Key[], selectedRows: any) => {
        setSelectsKeys(selectedRowKeys);

        const tempAwardList : Map<string, IAwardItem> = new Map<string, IAwardItem>();
        selectedRows.map((item: any) => {
          tempAwardList.set(item.memberCode, {awardCode:item.awardCode, name:item.memberEngName, memo:''});
        });
        setSelAwardList(tempAwardList);
      }
  };

  const onExportPdf = () => {
    if(selectsKeys.length === 0) {
        return infoNotification(t('class.award.message1'));
    } else {
        let isAwardChk = true;
        let nAwardMemberName = '';
        
        if(selAwardList.size > 0) {
          const nullAwardItems = Array.from(selAwardList.values()).filter(
            (item) => item.awardCode === null
          );

          if (nullAwardItems.length > 0) {
              isAwardChk = false;
              nAwardMemberName = nullAwardItems[0].name;
          }
        }

        if(isAwardChk) {
          setVisiblePdfDownload(true);
        } else {
          return infoNotification(nAwardMemberName + ' : ' + t('class.award.message2'));
        }
    }
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('class.class.column99'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
    },
    {
      title: t('class.class.column100'),
      dataIndex: 'memberEngName',
      key: 'memberEngName',
      align: 'center',
    },
    {
      title: t('class.class.column101'),
      dataIndex: 'awardCode',
      key: 'awardCode',
      align: 'center',
      render: (value, record, index) => (
        <Select
          className="w-150p"
          placeholder={t('common.button.select')}
          value={value}
          onChange={(value) => onSetAwardCode(index, value, record)}
        >
          {res?.data?.map((item: any) => {
            return (
              <Select.Option key={item.awardCode} value={item.awardCode}>
                {item.name}
              </Select.Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: t('class.class.column102'),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      render: (value) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClick={() => {
              setMemberCode(value);
              setAwardHistoryVisible(true);
            }}
            text={t('common.button.confirm')}
          />
        </div>
      ),
    },
    {
      title: t('class.class.button15'),
      dataIndex: 'awardCode',
      key: 'awardCode',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink-line"
            onClick={() => {
              window.open(`/class/print-award?awardCode=${value}&memberEngName=${record.memberEngName}`, '_blank');
            }}
            text={t('class.class.button15')}
            disabled={!value}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading || isLoading2 || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />

      <div className="comm-bx__between"> 
        <h3 className='sub-title'>{chosenClass?.className}</h3>
      </div>
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={data}
        rowKey={'memberCode'}
        rowSelection={{
            type: 'checkbox',
            ...rowSelection,
        }}
        pagination={{
          pageSize: 9999999,
        }}
      />
      <div className="comm-bx__space"> 
        <div className="comm-btn__area right">
          <DownloadPDFButton onClick={onExportPdf} btnText={'PDF DOWNLOAD'} />
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {awardHistoryVisible && (
        <AwardHistoryModal
          visible={awardHistoryVisible}
          setVisible={setAwardHistoryVisible}
          memberCode={memberCode}
          classCode={chosenClass?.classCode ?? ''}
        />
      )}

      <MakeAwardPDFModal
          visible={visiblePdfDownload}
          setVisible={setVisiblePdfDownload}
          selAwardList={selAwardList}
      />
    </>
  );
};

export default ClassAward;
